import { isSameRemediationItemFilterKey } from "../../../AppView/FilterManagement/filterKey";
import type {
  ApplyFilter,
  InOutCondition,
  InOutConditionValue,
  InOutFilterData,
  InOutFilterPart,
} from "./types";

// Conditions whose value is a list, so repeated clicks accumulate into it.
export const conditionsWithList = [
  "in",
  "not_in",
  "overlap",
  "notoverlapping",
  "overlapilikelist",
  "notoverlapilikelist",
  "overlapexactmatch",
  "likelist",
  "notlikelist",
  "notoverlapexactmatch",
];

export interface UseInOutButtonsOptions {
  filterData: InOutFilterData;
  applyFilter: ApplyFilter;
}

type FilterTuple = [
  filterCondition: string,
  field: string,
  value: InOutConditionValue,
];

export interface UseInOutButtonsReturnValue {
  onFilter: (
    filterCondition: string,
    field: string,
    value: InOutConditionValue,
    overrideConditionOnSame?: boolean,
    overrideIdenticalFieldAndCondition?: boolean,
    shouldReturnArray?: boolean
  ) => void;
  onMultipleFilters: (
    filtersToAdd: FilterTuple[],
    overrideConditionOnSame?: boolean,
    overrideIdenticalFieldAndCondition?: boolean,
    shouldReturnArray?: boolean
  ) => void;
}

const useInOutButtons = ({
  filterData,
  applyFilter,
}: UseInOutButtonsOptions): UseInOutButtonsReturnValue => {
  const handleConditionRow = (
    condition: string,
    conditionsArray: InOutCondition[],
    field: string,
    value: InOutConditionValue,
    overrideConditionOnSame?: boolean
  ): InOutCondition[] => {
    const isSameField = (filter: InOutCondition): boolean =>
      isSameRemediationItemFilterKey(filter.field, field);
    const conditionWithValuesList = conditionsWithList.includes(condition);

    if (conditionWithValuesList) {
      for (let index = conditionsArray.length - 1; index >= 0; index--) {
        const row = conditionsArray[index];
        if (
          isSameField(row) &&
          row.condition !== condition &&
          conditionsWithList.includes(row.condition)
        ) {
          row.value = (row.value as unknown[])?.filter(
            (itemValue) => itemValue !== value
          );
          if (!(row.value as unknown[])?.length) {
            conditionsArray.splice(index, 1);
          }
        }
      }
    }

    const isValueArray = Array.isArray(value);
    const rowsWithSameCondition = conditionsArray.filter(
      (filter) => isSameField(filter) && filter.condition === condition
    );

    if (rowsWithSameCondition.length && conditionWithValuesList) {
      rowsWithSameCondition.forEach((row) => {
        if (overrideConditionOnSame) {
          row.value = isValueArray ? [...value] : [value];
        } else if (!(row.value as unknown[])?.includes(value)) {
          (row.value as unknown[]).push(...(isValueArray ? value : [value]));
        }
      });
    } else {
      conditionsArray.push({
        field,
        condition: condition,
        value: conditionWithValuesList
          ? isValueArray
            ? value
            : [value]
          : value,
      });
    }

    return conditionsArray;
  };

  const getNewFilter = (
    filterCondition: string,
    field: string,
    value: InOutConditionValue,
    filterData: InOutFilterData,
    overrideConditionOnSame?: boolean,
    overrideIdenticalFieldAndCondition?: boolean,
    shouldReturnArray = true
  ): InOutFilterData => {
    if (!field || value === undefined || value === null || !filterCondition) {
      return filterData;
    }

    if (!filterData) {
      // Mirror handleConditionRow: only list conditions carry an array value.
      const shouldWrapInArray =
        shouldReturnArray && conditionsWithList.includes(filterCondition);
      filterData = [
        {
          conditions: [
            {
              field,
              condition: filterCondition,
              value: shouldWrapInArray
                ? Array.isArray(value)
                  ? [...value]
                  : [value]
                : value,
            },
          ],
        },
      ];
    } else {
      filterData?.forEach((filter, index) => {
        filterData![index].conditions = handleConditionRow(
          filterCondition,
          filter?.conditions,
          field,
          value,
          overrideConditionOnSame
        );
      });
    }

    // handeling the case where the same field with same condition is used with different value (overriding)
    if (overrideIdenticalFieldAndCondition) {
      const newConditions: InOutCondition[] = [];
      filterData?.forEach((filterDataPart: InOutFilterPart) => {
        filterDataPart?.conditions?.forEach((condition) => {
          const newCondition = newConditions?.find(
            (nc) =>
              (nc?.field === condition?.field ||
                isSameRemediationItemFilterKey(nc?.field, condition?.field)) &&
              nc?.condition === condition?.condition
          );
          if (newCondition) {
            newCondition.value = condition?.value;
          } else {
            newConditions.push(condition);
          }
        });
        filterDataPart.conditions = newConditions;
      });
    }
    return filterData;
  };

  const onFilter = (
    filterCondition: string,
    field: string,
    value: InOutConditionValue,
    overrideConditionOnSame = false,
    overrideIdenticalFieldAndCondition = false,
    shouldReturnArray = true
  ): void => {
    applyFilter(
      getNewFilter(
        filterCondition,
        field,
        value,
        filterData,
        overrideConditionOnSame,
        overrideIdenticalFieldAndCondition,
        shouldReturnArray
      ),
      field
    );
  };

  const onMultipleFilters = (
    filtersToAdd: FilterTuple[],
    overrideConditionOnSame?: boolean,
    overrideIdenticalFieldAndCondition?: boolean,
    shouldReturnArray = true
  ): void => {
    let newFilterData = filterData;
    filtersToAdd.forEach(([filterCondition, field, value]) => {
      newFilterData = getNewFilter(
        filterCondition,
        field,
        value,
        newFilterData,
        overrideConditionOnSame,
        overrideIdenticalFieldAndCondition,
        shouldReturnArray
      );
    });

    applyFilter(newFilterData);
  };

  return { onFilter, onMultipleFilters };
};

export default useInOutButtons;
